/**
 * Classic roster (04 §1, 01): the 1987 structure - six persistent slots, Assign / Retire. A
 * vacant slot recruits a ranger by typing his name; a filled one assigns him to a mission or,
 * after a confirmation, retires him. The dead stay on the roster until their slot is cleared.
 */

import '../rosters.css';
import type { RangerRecord } from '../../../meta/types';
import type { Ctx, Screen } from '../context';
import { box, button, el, errorMessage, show, textField } from '../dom';
import { availability, serviceName } from '../text';
import { confirm, dialog, notice } from '../widgets/confirm';
import { dossierCard } from '../widgets/dossierCard';
import { frame } from '../widgets/frame';

export const CLASSIC_SLOTS = 6;
const NAME_MAX = 14;

const RULES = 'ORIGINAL RULES: NO PERKS, TRAITS, REQUISITION OR DOWNED STATE. THE THIRD WOUND KILLS.'
  + ' PODS ARE PACKED FREE.';

/** The recruit dialog: three fields; a ranger needs at least a first name and a surname. */
function recruit(ctx: Ctx): void {
  const close = (): void => ctx.closeModal();
  const d = dialog('RECRUIT A RANGER', close);
  const first = textField('FIRST NAME', '', NAME_MAX);
  const surname = textField('SURNAME', '', NAME_MAX);
  const callsign = textField('CALLSIGN', '', NAME_MAX);
  const error = el('div', 'menu-error menu-hidden');
  d.body.append(
    el('div', 'menu-prose menu-dim', 'NAME HIM. PEOPLE NAME THEM AFTER FRIENDS - THAT IS THE POINT.'),
    first.row,
    surname.row,
    callsign.row,
    error,
  );
  const refuse = (why: string): void => {
    error.textContent = why;
    show(error, true);
  };
  const enlist = button('ENLIST', () => {
    const a = first.input.value.trim();
    const b = surname.input.value.trim();
    if (a === '' || b === '') {
      refuse('A RANGER NEEDS A FIRST NAME AND A SURNAME.');
      (a === '' ? first.input : surname.input).focus();
      return;
    }
    try {
      ctx.api.recruitClassic(a, b, callsign.input.value.trim());
    } catch (err) {
      refuse(errorMessage(err));
      return;
    }
    close();
    ctx.nav.classic();
  });
  d.actions.append(button('CANCEL', close), enlist);
  ctx.openModal({ ...d.modal, initialFocus: () => first.input });
}

function retire(ctx: Ctx, r: RangerRecord, alive: boolean): void {
  confirm(ctx, {
    title: alive ? `RETIRE ${serviceName(r)}?` : `CLEAR ${serviceName(r)} FROM THE ROSTER?`,
    lines: alive
      ? [
          'THIS IS PERMANENT. HIS RANK, MEDALS AND RECORD LEAVE THE ROSTER WITH HIM.',
          'THE SLOT OPENS FOR A NEW RECRUIT.',
        ]
      : ['HIS SLOT OPENS FOR A NEW RECRUIT. THIS CANNOT BE UNDONE.'],
    confirmLabel: alive ? 'RETIRE HIM' : 'CLEAR THE SLOT',
    danger: true,
    onConfirm: () => {
      try {
        ctx.api.retireClassic(r.id);
      } catch (err) {
        notice(ctx, 'NOT DONE', [errorMessage(err)]);
        return;
      }
      ctx.nav.classic();
    },
  });
}

export function classicRosterScreen(ctx: Ctx): Screen {
  const roster = ctx.api.profile().classicRoster;
  const f = frame('classic', 'CLASSIC - ROSTER');
  f.info.append(el('span', '', `${Math.min(roster.length, CLASSIC_SLOTS)}/${CLASSIC_SLOTS} SLOTS`));

  const grid = el('div', 'menu-card-grid menu-scroll menu-grow');
  let firstAssign: HTMLButtonElement | null = null;
  let firstRecruit: HTMLButtonElement | null = null;

  for (const r of roster) {
    const state = availability(r);
    const alive = r.status !== 'kia' && r.status !== 'lost';
    const card = dossierCard(r, { describe: ctx.describe });
    const actions = el('div', 'menu-dossier-actions');
    if (state.available) {
      const assign = button('ASSIGN', () => ctx.nav.classicMissions(r.id), 'menu-btn-small');
      firstAssign ??= assign;
      actions.append(assign);
    }
    actions.append(
      button(alive ? 'RETIRE' : 'CLEAR SLOT', () => retire(ctx, r, alive), 'menu-btn-small menu-btn-danger'),
    );
    card.append(actions);
    grid.append(card);
  }
  for (let slot = roster.length; slot < CLASSIC_SLOTS; slot++) {
    const open = button('RECRUIT', () => recruit(ctx), 'menu-btn-small');
    firstRecruit ??= open;
    grid.append(box('menu-slot-empty', el('div', 'menu-dim', `SLOT ${slot + 1} - VACANT`), open));
  }

  const back = button('BACK', () => ctx.nav.title());
  const foot = box('menu-row menu-spread', el('div', 'menu-prose menu-dim menu-small menu-grow', RULES), back);
  f.body.append(box('menu-col menu-grow', grid, foot));
  return {
    el: f.el,
    back: () => ctx.nav.title(),
    initialFocus: () => firstAssign ?? firstRecruit ?? back,
  };
}
